/* eslint-disable import/first */
/* eslint no-console:0 */

// CSRF Token Refresh for Application Forms
// Refreshes the CSRF token before form submission if the page has been open too long
// or if the company setting requires it (for long-running sessions)

const DEFAULT_STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

interface FormConfig {
  refreshUrl: string;
  staleThresholdMs: number;
  alwaysRefresh: boolean;
}

function updateCsrfMetaTag(token: string): void {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    metaTag.setAttribute('content', token);
  }
}

async function refreshCsrfToken(url: string): Promise<string | null> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  });

  const data = await response.json();
  if (data.csrf_token) {
    updateCsrfMetaTag(data.csrf_token);
    return data.csrf_token;
  }
  return null;
}

function getFormConfig(form: HTMLFormElement): FormConfig | null {
  const refreshUrl = form.dataset.csrfRefreshUrl;
  if (!refreshUrl) return null;

  return {
    refreshUrl,
    staleThresholdMs: Number(form.dataset.csrfStaleThresholdMs) || DEFAULT_STALE_THRESHOLD_MS,
    alwaysRefresh: form.dataset.csrfAlwaysRefresh === 'true',
  };
}

function setupCsrfRefresh(form: HTMLFormElement, config: FormConfig): void {
  const pageLoadTime = Date.now();
  let isRefreshing = false;

  form.addEventListener('submit', async (e) => {
    if (isRefreshing) return;

    const timeSinceLoad = Date.now() - pageLoadTime;
    const shouldRefresh = config.alwaysRefresh || timeSinceLoad >= config.staleThresholdMs;
    if (!shouldRefresh) return;

    const tokenField = form.querySelector<HTMLInputElement>('input[name="authenticity_token"]');
    if (!tokenField) return;

    e.preventDefault();
    isRefreshing = true;

    try {
      const newToken = await refreshCsrfToken(config.refreshUrl);
      if (newToken) {
        tokenField.value = newToken;
      }
    } catch (error) {
      console.error('CSRF token refresh failed:', error);
    }

    form.submit();
  });
}

function initializeCsrfRefresh(): void {
  const forms = document.querySelectorAll<HTMLFormElement>('form[data-csrf-refresh-url]');

  forms.forEach((form) => {
    const config = getFormConfig(form);
    if (config) {
      setupCsrfRefresh(form, config);
    }
  });
}

document.addEventListener('turbolinks:load', initializeCsrfRefresh);
